import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for Server-Sent Events (SSE) connection
 * Handles connection, reconnection, and cleanup automatically
 */
export const useSSE = (sessionId, onProgress, onComplete, onError, options = {}) => {
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  
  const {
    maxReconnectAttempts = 3,
    reconnectDelay = 2000,
    apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000',
    autoReconnect = true
  } = options;

  const cleanup = useCallback(() => {
    console.log('[useSSE] 🧹 Cleaning up SSE connection');
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback(() => {
    if (!sessionId) {
      console.log('[useSSE] ⚠️ No session ID provided, skipping connection');
      return;
    }

    // Clean up any existing connection
    cleanup();

    console.log(`[useSSE] 🔗 Connecting to SSE stream for session: ${sessionId}`);
    
    try {
      // Get auth token
      const token = localStorage.getItem('userToken');
      if (!token) {
        console.error('[useSSE] ❌ No authentication token found');
        onError?.({ type: 'auth', message: 'Authentication required' });
        return;
      }

      // Create EventSource with auth headers (note: EventSource doesn't support custom headers natively)
      const url = `${apiUrl}/api/agent/ask/stream/${sessionId}?token=${encodeURIComponent(token)}`;
      
      // For better auth support, we could use a library like eventsource or fetch with streaming
      // For now, let's use the native EventSource and handle auth via query param
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('[useSSE] ✅ SSE connection established');
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          console.log('[useSSE] 📨 Received SSE message:', event.data);
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            console.log('[useSSE] 🎉 Connection confirmed');
            return;
          }
          
          if (data.type === 'progress') {
            console.log(`[useSSE] 📊 Progress: ${data.step} (${data.percentage}%)`);
            onProgress?.(data);
          } else if (data.type === 'complete') {
            console.log('[useSSE] ✅ Processing complete');
            // The payload lives in frame.data; carry visualizations from the
            // frame level too so a stray envelope change can't drop the chart.
            const payload = data.data && typeof data.data === 'object' ? data.data : data;
            onComplete?.({
              ...payload,
              visualizations: payload.visualizations ?? data.visualizations
            });
            cleanup();
          } else if (data.type === 'error') {
            console.error('[useSSE] ❌ Server error:', data.message);
            onError?.(data);
            cleanup();
          }
        } catch (error) {
          console.error('[useSSE] ❌ Error parsing SSE message:', error);
          onError?.({ type: 'parse', message: 'Failed to parse server response' });
        }
      };

      eventSource.onerror = (error) => {
        console.error('[useSSE] ❌ SSE connection error:', error);
        
        // Check if we should attempt reconnection
        if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(`[useSSE] 🔄 Attempting reconnection (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else {
          console.log('[useSSE] ❌ Max reconnection attempts reached or auto-reconnect disabled');
          onError?.({ 
            type: 'connection', 
            message: 'Connection lost and could not reconnect',
            reconnectAttempts: reconnectAttemptsRef.current
          });
          cleanup();
        }
      };

    } catch (error) {
      console.error('[useSSE] ❌ Error creating SSE connection:', error);
      onError?.({ type: 'connection', message: 'Failed to establish connection' });
    }
  }, [sessionId, apiUrl, onProgress, onComplete, onError, autoReconnect, maxReconnectAttempts, reconnectDelay, cleanup]);

  // Connect when sessionId changes
  useEffect(() => {
    if (sessionId) {
      connect();
    }
    
    // Cleanup on unmount or sessionId change
    return cleanup;
  }, [sessionId, connect, cleanup]);

  // Manual reconnection function
  const reconnect = useCallback(() => {
    console.log('[useSSE] 🔄 Manual reconnection requested');
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // Get connection status
  const getConnectionStatus = useCallback(() => {
    if (!eventSourceRef.current) return 'disconnected';
    
    const readyState = eventSourceRef.current.readyState;
    switch (readyState) {
      case EventSource.CONNECTING: return 'connecting';
      case EventSource.OPEN: return 'connected';
      case EventSource.CLOSED: return 'disconnected';
      default: return 'unknown';
    }
  }, []);

  return {
    connect,
    disconnect: cleanup,
    reconnect,
    getConnectionStatus,
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN
  };
};

export default useSSE;